import { useQuery } from "@tanstack/react-query";
import { useSettingTournamentApplyDetail } from "../../../@apis/hooks/useSetting";
import type { TournamentQrParams } from "../../../@apis/dto/request/tournament.request";
import { formatDate } from "../../../lib/utils/dateUtils";
import { cn } from "../../../lib/utils/cn";

export type ApplyQrInfoRow = { label: string; value: string };

/* 현장 인증 화면(QR / 인증 완료)이 공통으로 보여주는 접수자 정보.
 * 접수 상세와 같은 쿼리 키를 쓰므로 추가 요청은 발생하지 않는다.
 */
export function useApplyQrInfo(
  tournamentId: string | number,
  applyId: string | number,
  qrParams?: TournamentQrParams | null,
) {
  const { data } = useQuery({
    ...useSettingTournamentApplyDetail(tournamentId, applyId),
    enabled: !!tournamentId && !!applyId,
  });

  const tournament = data?.data?.tournamentInfo;
  const assignments = data?.data?.assignments ?? [];
  // 라운드 대회는 모든 라운드의 편성이 내려오므로 QR 대상 라운드의 편성만 고른다.
  // (대상 라운드 편성이 없으면 표시하지 않는다) 그 외 대회는 신청한 일정의 편성 1건이라 첫 번째를 쓴다.
  const assignment =
    qrParams?.roundId != null
      ? assignments.find((a) => a.roundId === Number(qrParams.roundId))
      : assignments[0];
  const eventDate = assignment?.date ?? tournament?.calendarInfo?.date;

  return {
    applyNo: tournament?.apply?.id ?? applyId,
    rows: [
      { label: "이름", value: data?.data?.userInfo?.name || "-" },
      {
        label: "대회날짜",
        value: eventDate ? formatDate(eventDate, "YYYY-MM-DD (ddd)") : "-",
      },
      {
        label: "대회일정",
        value:
          assignment?.roundName || tournament?.calendarInfo?.timeType || "-",
      },
      { label: "그룹", value: assignment?.groupName || "-" },
      { label: "조", value: assignment?.teamName || "-" },
    ],
  };
}

export default function ApplyQrInfoCard({
  rows,
  className,
}: {
  rows: ApplyQrInfoRow[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-[4px] rounded-[10px] px-[20px] py-[12px]",
        className,
      )}
    >
      {rows.map(({ label, value }) => (
        <div
          key={label}
          className="flex items-start gap-[8px] text-sm font-medium"
        >
          <p className="min-w-[80px] shrink-0 text-[#8e8e93]">{label}</p>
          <p className="flex-1 min-w-0 text-black break-keep [overflow-wrap:anywhere]">
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}
